/*
 * Copyright (C) 2025-2026 The Klangmotor Authors (see AUTHORS.MD)
 * SPDX-License-Identifier: AGPL-3.0-or-later
 */

@file:Suppress("DuplicatedCode", "ObjectPropertyName", "Detekt:TooManyFunctions", "ClassName")
@file:KlangScript.Library("sprudel")

package io.peekandpoke.klang.sprudel.lang

import io.peekandpoke.klang.audio_bridge.constants.DELAY_CAP
import io.peekandpoke.klang.audio_bridge.constants.DELAY_FEEDBACK
import io.peekandpoke.klang.audio_bridge.constants.DELAY_TIME_SECONDS
import io.peekandpoke.klang.audio_bridge.constants.DELAY_WET
import io.peekandpoke.klang.script.annotations.KlangScript
import io.peekandpoke.klang.script.ast.CallInfo
import io.peekandpoke.klang.sprudel.SprudelPattern
import io.peekandpoke.klang.sprudel.SprudelVoiceData
import io.peekandpoke.klang.sprudel._liftOrReinterpretNumericalField
import io.peekandpoke.klang.sprudel._mapNumericField
import io.peekandpoke.klang.sprudel.katalystParamsOrNew
import io.peekandpoke.klang.sprudel.lang.SprudelDslArg.Companion.asSprudelDslArgs

// -- the call sets every slot ----------------------------------------------------------------------------------------

/**
 * Fills the delay stage's companions from `constants/BusEffectDefaults.kt`, the same constants the
 * master delay uses (`/dsl-design` §4 is the rule; this is only what THIS door does). Called from
 * every delay setter, because the delay has no name knob: `delay(time = 0.5)` fills `wet` and the
 * echo is ON.
 *
 * The slots are the delay's only storage since Katalyst step 5b-3 (the voice fields left the
 * wire and `SprudelVoiceData`): the orbit's line reads them, and so do the `delay.*` accessors.
 *
 * Byte-identical to what the engine did with an unset field until then: `VoiceFactory`
 * substituted exactly these constants.
 *
 * The HEAD setter, the wet, is the one setter here a bare call can reach with a null; it then
 * writes nothing at all (`if (wet != null)`).
 */
private fun SprudelVoiceData.fillDelayDefaults() {
    val slots = katalystParamsOrNew()

    slots.setOrDefault("delay.wet", value = null, default = DELAY_WET)
    slots.setOrDefault("delay.time", value = null, default = DELAY_TIME_SECONDS)
    slots.setOrDefault("delay.feedback", value = null, default = DELAY_FEEDBACK)
    slots.setOrDefault("delay.cap", value = null, default = DELAY_CAP)
}

// -- delay, the wet slot ---------------------------------------------------------------------------------------------

private val delayMutation = voiceSetter {
    val wet = it?.toString()?.toDoubleOrNull()

    if (wet != null) {
        katalystParamsOrNew().set("delay.wet", wet)
        fillDelayDefaults()
    }
}

private fun applyDelay(source: SprudelPattern, args: List<SprudelDslArg<Any?>>): SprudelPattern {
    args.singleMapperOrNull()?.let { mapper ->
        return source._mapNumericField(mapper, read = { it.katalystParams?.get("delay.wet") }, update = delayMutation)
    }

    return source._liftOrReinterpretNumericalField(args, delayMutation)
}


/**
 * The orbit delay: how much of the orbit is echoed, the time, the feedback and the feedback cap.
 *
 * One delay per orbit bus, and all four are set once for everyone by
 * the orbit's owning voice. `wet` is how much of the orbit goes into the delay: the echoes are
 * added on top of the dry sound, and every voice on the orbit is echoed alike. So a pattern that
 * should stay dry, or echo by a different amount, goes on an orbit of its own. When another pattern
 * with a delay takes the orbit over, `wet` and `feedback` move smoothly to its values, and a new
 * time crossfades to the new echo; a pattern without one switches the delay off: its feed fades
 * out over 50 ms and the echoes already sent ring out.
 *
 * The call sets every slot: the ones you leave out take the same default as on the master delay,
 * wet 0.25, time 0.25 s, feedback 0.3 and cap 1, unless an earlier call already set them. So a bare
 * `delay(0.4)` already echoes a quarter second later, and `delay.time` reads 0.25 after it. Slots
 * apply in order, wet first, so a mapper on a later slot sees a default an earlier slot of the same
 * call filled in. A call whose only slot rests in its control pattern writes nothing on that event,
 * and fills nothing.
 *
 * Every slot is independent and patternable; an omitted slot keeps its value, a named slot takes
 * a mapper (`delay(time = mul(2))`), and the numeric slots read back as `delay.wet`, `delay.time`, `delay.feedback`, `delay.cap`.
 * With no argument at all, the pattern's own values are reinterpreted as `wet`.
 *
 * ```KlangScript(Playable)
 * s("hh*4").delay(0.3, 0.25, 0.4)                                        // wet, time, feedback
 * ```
 *
 * ```KlangScript(Playable)
 * stack(s("hh ~ hh ~").delay(0.3, 0.25), s("~ hh ~ hh").orbit(1))        // delay on every other hat only
 * ```
 *
 * ```KlangScript(Playable)
 * s("sd sd").delay("<0.1 0.4>", 0.25).reverb(wet = delay.wet, size = 4)     // as much reverb as delay
 * ```
 *
 * @param wet How much of the orbit goes into the delay, 0 to 1, default 0.25. Orbit-wide.
 * @param time Delay time in seconds, default 0.25. Orbit-wide.
 * @param feedback Feedback, 0 to 1, default 0.3. Above 1 builds up. Orbit-wide.
 * @param cap Ceiling the repeats may not exceed, default 1. Orbit-wide.
 * @tool SprudelDelayEditor
 * @tool wet: SprudelDelayEditor, SprudelDelaySequenceEditor
 * @tool time: SprudelDelayTimeEditor, SprudelDelayTimeSequenceEditor
 * @tool feedback: SprudelDelayFeedbackEditor, SprudelDelayFeedbackSequenceEditor
 *
 * @scope orbit
 * @category effects
 * @tags delay, wet, time, feedback, cap
 */
@KlangScript.Function
fun SprudelPattern.delay(
    wet: PatternLike? = null,
    time: PatternLike? = null,
    feedback: PatternLike? = null,
    cap: PatternLike? = null,
    callInfo: CallInfo? = null
): SprudelPattern {
    // A tail-only call must not touch wet: reinterpret runs only on a fully bare call.
    var p = if (wet != null || !(time != null || feedback != null || cap != null)) {
        applyDelay(this, listOfNotNull(wet).asSprudelDslArgs(callInfo))
    } else {
        this
    }
    if (time != null) p = applyDelayTime(p, listOf<Any?>(time).asSprudelDslArgs(callInfo?.forParam(1)))
    if (feedback != null) p = applyDelayFeedback(p, listOf<Any?>(feedback).asSprudelDslArgs(callInfo?.forParam(2)))
    if (cap != null) p = applyDelayCap(p, listOf<Any?>(cap).asSprudelDslArgs(callInfo?.forParam(3)))
    return p
}

/** Parses this string as a pattern, then applies [delay]. */
@KlangScript.Function
fun String.delay(
    wet: PatternLike? = null,
    time: PatternLike? = null,
    feedback: PatternLike? = null,
    cap: PatternLike? = null,
    callInfo: CallInfo? = null
): SprudelPattern =
    this.toVoiceValuePattern(callInfo?.receiverLocation).delay(wet, time, feedback, cap, callInfo)

/** Chains a [delay] step onto this [PatternMapperFn]. */
@KlangScript.Function
fun PatternMapperFn.delay(
    wet: PatternLike? = null,
    time: PatternLike? = null,
    feedback: PatternLike? = null,
    cap: PatternLike? = null,
    callInfo: CallInfo? = null
): PatternMapperFn =
    this.chain { p -> p.delay(wet, time, feedback, cap, callInfo) }

/**
 * The `delay` object: `delay(...)` sets the slots, and each numeric slot reads back as a child,
 * `delay.wet`, `delay.time`, `delay.feedback`, `delay.cap`.
 *
 * @scope orbit
 * @category effects
 * @tags delay, accessor
 */
@KlangScript.Library("sprudel")
@KlangScript.Object("delay")
object delay {

    /** The wet slot of each event, as a value other setters can read. */
    @KlangScript.Property
    val wet: FieldAccessor = FieldAccessor { it.katalystParams?.get("delay.wet") }

    /** The time slot of each event, as a value other setters can read. */
    @KlangScript.Property
    val time: FieldAccessor = FieldAccessor { it.katalystParams?.get("delay.time") }

    /** The feedback slot of each event, as a value other setters can read. */
    @KlangScript.Property
    val feedback: FieldAccessor = FieldAccessor { it.katalystParams?.get("delay.feedback") }

    /** The cap slot of each event, as a value other setters can read. */
    @KlangScript.Property
    val cap: FieldAccessor = FieldAccessor { it.katalystParams?.get("delay.cap") }

    /**
     * The setter, see [SprudelPattern.delay].
     *
     * @param wet How much of the orbit goes into the delay, 0 to 1, default 0.25. Orbit-wide.
     * @param time Delay time in seconds, default 0.25. Orbit-wide.
     * @param feedback Feedback, 0 to 1, default 0.3. Above 1 builds up. Orbit-wide.
     * @param cap Ceiling the repeats may not exceed, default 1. Orbit-wide.
     */
    @KlangScript.Invoke
    operator fun invoke(
        wet: PatternLike? = null,
        time: PatternLike? = null,
        feedback: PatternLike? = null,
        cap: PatternLike? = null,
        callInfo: CallInfo? = null
    ): PatternMapperFn =
        { p -> p.delay(wet, time, feedback, cap, callInfo) }
}

// -- delay.time ------------------------------------------------------------------------------------------------------

private val delayTimeMutation = voiceSetter {
    val value = it?.asDoubleOrNull()

    if (value != null) {
        katalystParamsOrNew().set("delay.time", value)
        fillDelayDefaults()
    }
}

private fun applyDelayTime(source: SprudelPattern, args: List<SprudelDslArg<Any?>>): SprudelPattern {
    args.singleMapperOrNull()?.let { mapper ->
        return source._mapNumericField(mapper, read = { it.katalystParams?.get("delay.time") }, update = delayTimeMutation)
    }

    return source._liftOrReinterpretNumericalField(args, delayTimeMutation)
}

// -- delay.feedback --------------------------------------------------------------------------------------------------

private val delayFeedbackMutation = voiceSetter {
    val value = it?.asDoubleOrNull()

    if (value != null) {
        katalystParamsOrNew().set("delay.feedback", value)
        fillDelayDefaults()
    }
}

private fun applyDelayFeedback(source: SprudelPattern, args: List<SprudelDslArg<Any?>>): SprudelPattern {
    args.singleMapperOrNull()?.let { mapper ->
        return source._mapNumericField(mapper, read = { it.katalystParams?.get("delay.feedback") }, update = delayFeedbackMutation)
    }

    return source._liftOrReinterpretNumericalField(args, delayFeedbackMutation)
}

// -- delay.cap -------------------------------------------------------------------------------------------------------

private val delayCapMutation = voiceSetter {
    val value = it?.asDoubleOrNull()

    if (value != null) {
        katalystParamsOrNew().set("delay.cap", value)
        fillDelayDefaults()
    }
}

private fun applyDelayCap(source: SprudelPattern, args: List<SprudelDslArg<Any?>>): SprudelPattern {
    args.singleMapperOrNull()?.let { mapper ->
        return source._mapNumericField(mapper, read = { it.katalystParams?.get("delay.cap") }, update = delayCapMutation)
    }

    return source._liftOrReinterpretNumericalField(args, delayCapMutation)
}
